// @ts-nocheck
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, Link, useParams } from "react-router-dom";
import {
  ChevronRight, ChevronLeft, Check, Search, Plus,
  Trash2, X, ChevronDown, ChevronUp, Upload, Eye,
  Edit2, Contact, Home, DollarSign, AlertTriangle, CheckCircle2, Loader2, AlertCircle, Info, Edit3, Sparkles
} from "lucide-react";
import {
  collection, doc, runTransaction, serverTimestamp, getDocs, query, where, getDoc, updateDoc, arrayUnion
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, storageEnvironment } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { PROFISSOES_CBO } from "../../utils/profissoesCBO";

// ============================================================================
// COMPONENTE PORTAL SEGURO E TOGGLES HIGIENIZADOS (Sem Warnings)
// ============================================================================

/** @param {{ children: React.ReactNode }} props */
const SafePortal = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  if (!mounted) return null;
  return createPortal(children, document.body);
};

/**
 * @param {Object} props
 * @param {boolean} props.checked
 * @param {(val: boolean) => void} props.onChange
 * @param {string} [props.label]
 */
const CustomToggle = ({ checked, onChange, label }) => (
  <button type="button" className="flex items-center cursor-pointer focus:outline-none" onClick={() => onChange(!checked)}>
    <div className={`relative w-9 h-4 sm:w-10 sm:h-5 rounded-full transition-colors duration-300 border ${checked ? 'bg-brand-primary border-brand-primary' : 'bg-gray-300 border-gray-300 shadow-inner'}`}>
      <div className={`absolute top-[1px] left-[1px] w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full shadow-sm transition-transform duration-300 ${checked ? 'transform translate-x-5' : ''}`}></div>
    </div>
    {label && <span className="ml-3 text-xs sm:text-sm text-slate-500 font-bold">{label}</span>}
  </button>
);

/**
 * @param {Object} props
 * @param {boolean} props.checked
 * @param {(val: boolean) => void} props.onChange
 */
const EvalToggle = ({ checked, onChange }) => (
  <button type="button" className="flex items-center cursor-pointer focus:outline-none" onClick={() => onChange(!checked)}>
    <div className={`relative w-9 h-4 sm:w-10 sm:h-5 rounded-full transition-colors duration-300 border ${checked ? 'bg-brand-primary border-brand-primary' : 'bg-red-500 border-red-500'}`}>
      <div className={`absolute top-[1px] left-[1px] w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full shadow-sm transition-transform duration-300 ${checked ? 'transform translate-x-5' : ''}`}></div>
    </div>
  </button>
);

// ============================================================================
// FUNÇÕES DE MÁSCARA E SANITIZAÇÃO (Fase 3: Integridade)
// ============================================================================

/** @param {string | number} value */
const maskCurrency = (value) => {
  if (value === undefined || value === null) return "";
  let v = String(value).replace(/\D/g, "");
  if (v === "") return "";
  v = (Number(v) / 100).toFixed(2) + "";
  v = v.replace(".", ",");
  v = v.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
  return v;
};

/** @param {string} value */
const maskCpfCnpj = (value) => {
  if (!value) return "";
  let v = String(value).replace(/\D/g, "");
  if (v.length <= 11) {
    return v.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  } else {
    return v.replace(/(\d{2})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1/$2").replace(/(\d{4})(\d{1,2})$/, "$1-$2").slice(0, 18);
  }
};

/** @param {string} value */
const maskCEP = (value) => {
  if (!value) return "";
  return String(value).replace(/\D/g, "").replace(/^(\d{5})(\d)/, "$1-$2").slice(0, 9);
};

/** @param {string} value */
const maskPhone = (value) => {
  if (!value) return "";
  let v = String(value).replace(/\D/g, "");
  if (v.length <= 10) {
    return v.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  } else {
    return v.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2").slice(0, 15);
  }
};

/** @param {string | Date} dataNascimento */
const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  const hoje = new Date();
  const nascimento = new Date(dataNascimento);
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const m = hoje.getMonth() - nascimento.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nascimento.getDate())) { idade--; }
  return idade >= 0 ? idade : null;
};

// --- SANITIZADORES DE PAYLOAD ---
/** @param {any} value */
const unmaskCurrency = (value) => {
  if (!value) return 0;
  return parseFloat(String(value).replace(/\./g, '').replace(',', '.'));
};

/** @param {any} val */
const loadCurrency = (val) => {
  if (val === null || val === undefined || val === "") return "";
  return maskCurrency((Number(val) * 100).toFixed(0));
};

/** @param {any} val */
const unmaskStr = (val) => String(val || "").replace(/\D/g, "");

// ============================================================================
// WIZARD DE NOVO REQUEST
// ============================================================================

const NovaSolicitacaoPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const protocoloReal = id ? id.replace('-', '/') : "";

  // --- ESTADOS DE LGPD & INFRAESTRUTURA ---
  const [clientIp, setClientIp] = useState("");
  const [aceiteTermos, setAceiteTermos] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [isLoadingEdicao, setIsLoadingEdicao] = useState(!!id);
  const [ultimoParecer, setUltimoParecer] = useState(null);

  const [isSearchingCpf, setIsSearchingCpf] = useState(false);
  const [isSearchingProprietario, setIsSearchingProprietario] = useState(false);
  const [showProfissoes, setShowProfissoes] = useState(false);
  const [customProfissoes, setCustomProfissoes] = useState(/** @type {string[]} */ ([]));
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { currentUser, userInfo, companyInfo } = useAuth();

  const [formData, setFormData] = useState({
    _idBanco: "",
    numeroSolicitacao: "AGUARDANDO",
    dataSolicitacao: "",
    servico: "",
    isLegacy: false, // FASE 5: Chave Seletora de Cadastro Legado
    imovel: {
      codigo: "", cpfCnpjProprietario: "", nomeProprietario: "", aluguel: "", condominio: "", iptu: "", agua: "", luz: "", seguroIncendio: "", tipo: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", estado: "", cidade: ""
    },
    observacoes: "",
    taxaAtivacao: "",
    partes: /** @type {any[]} */ ([])
  });

  const VALORES_COMISSAO = ["10,00", "50,00", "98,00", "150,00", "200,00", "225,00", "250,00", "275,00", "300,00"];

  // --- CAPTURA DE IP SILENCIOSA (LGPD) ---
  useEffect(() => {
    const fetchIp = async () => {
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        const data = await response.json();
        setClientIp(data.ip);
      } catch (error) {
        console.error("Erro ao capturar IP:", error);
        setClientIp("IP_NAO_CAPTURADO");
      }
    };
    fetchIp();
  }, []);

  useEffect(() => {
    if (companyInfo?.id) {
      const fetchCustomProfissoes = async () => {
        try {
          const compRef = doc(db, "companies", companyInfo.id);
          const compSnap = await getDoc(compRef);
          if (compSnap.exists() && compSnap.data().customProfissoes) {
            setCustomProfissoes(compSnap.data().customProfissoes);
          }
        } catch (err) {
          const error = /** @type {Error} */ (err);
          console.error("Erro ao buscar profissões customizadas:", error.message);
        }
      };
      fetchCustomProfissoes();
    }
  }, [companyInfo?.id]);

  useEffect(() => {
    if (id) {
      if (!companyInfo?.id) return;

      const loadEditData = async () => {
        try {
          const q = query(
            collection(db, "requests"),
            where("protocolo", "==", protocoloReal),
            where("companyId", "==", companyInfo.id)
          );
          
          const snapshot = await getDocs(q);
          if (!snapshot.empty) {
            const docData = snapshot.docs[0].data();
            const docId = snapshot.docs[0].id;
            
            if (docData.status !== "Pendente") {
              alert("Este request não está mais pendente e não pode ser editado.");
              navigate("/dashboard/requests");
              return;
            }

            /** @type {Array<any>} */
            let partesData = [];
            try {
              const partesRef = collection(db, `requests/${docId}/partes`);
              const partesSnap = await getDocs(partesRef);
              partesData = partesSnap.docs.map(pDoc => ({ id: pDoc.id, ...pDoc.data() }));
            } catch (err) {
              const error = /** @type {Error} */ (err);
              console.error("Erro ao buscar partes para edição:", error.message);
            }

            if (docData.historico && docData.historico.length > 0) {
               const ultHist = [...docData.historico].reverse().find(h => h.status === 'Pendente');
               if (ultHist) setUltimoParecer(ultHist.mensagem);
            }

            // CARREGAMENTO SEGURO: Injeção de máscaras aos dados do banco
            setFormData(prev => ({
              ...prev,
              _idBanco: docId,
              numeroSolicitacao: docData.protocolo,
              dataSolicitacao: docData.dataSolicitacaoTexto,
              servico: docData.servico || "",
              isLegacy: Boolean(docData.isLegacy),
              imovel: {
                ...docData.imovel,
                cpfCnpjProprietario: maskCpfCnpj(docData.imovel?.cpfCnpjProprietario),
                cep: maskCEP(docData.imovel?.cep),
                aluguel: loadCurrency(docData.imovel?.aluguel),
                condominio: loadCurrency(docData.imovel?.condominio),
                iptu: loadCurrency(docData.imovel?.agua),
                agua: loadCurrency(docData.imovel?.agua),
                luz: loadCurrency(docData.imovel?.luz),
                seguroIncendio: loadCurrency(docData.imovel?.seguroIncendio)
              },
              observacoes: docData.observacoes || "",
              taxaAtivacao: loadCurrency(docData.taxaAtivacao) || "",
              partes: partesData.map(p => ({
                ...p,
                dadosPessoais: {
                  ...p.dadosPessoais,
                  cpf: maskCpfCnpj(p.dadosPessoais?.cpf),
                  telefone: maskPhone(p.dadosPessoais?.telefone)
                },
                endereco: {
                  ...p.endereco,
                  cep: maskCEP(p.endereco?.cep)
                },
                perfilFinanceiro: {
                  ...p.perfilFinanceiro,
                  rendas: (p.perfilFinanceiro?.rendas || []).map(/** @param {any} r */ r => ({
                    ...r,
                    valor: loadCurrency(r.valor)
                  }))
                }
              }))
            }));
            
          } else {
            alert("Request não encontrado ou acesso negado.");
            navigate("/dashboard/requests");
          }
        } catch (err) {
          const error = /** @type {Error} */ (err);
          console.error("Erro ao carregar edição", error.message);
        } finally {
          setIsLoadingEdicao(false);
        }
      };

      loadEditData();
    } else {
      const now = new Date();
      const dataFormatada = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()} às ${now.getHours().toString().padStart(2, '0')}h${now.getMinutes().toString().padStart(2, '0')}`;
      setFormData(prev => ({ ...prev, dataSolicitacao: dataFormatada }));
      setIsLoadingEdicao(false);
    }
  }, [id, protocoloReal, companyInfo?.id, navigate]);

  const canAdvance = () => {
    if (currentStep === 1) return !!formData.servico;
    if (currentStep === 2) return true;
    if (currentStep === 3) return true;
    if (currentStep === 4) return true;
    return false;
  };

  const [loadingCepImovel, setLoadingCepImovel] = useState(false);
  const buscarCepImovel = async () => {
    const cepNumeros = formData.imovel.cep.replace(/\D/g, '');
    if (cepNumeros.length !== 8) return;
    
    setLoadingCepImovel(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepNumeros}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setFormData(prev => ({
          ...prev, imovel: {
            ...prev.imovel, endereco: data.logradouro, bairro: data.bairro, cidade: data.localidade, estado: data.uf
          }
        }));
      }
    } catch (err) {
       const error = /** @type {Error} */ (err);
      console.error("Erro ao buscar CEP", error.message);
    } finally {
      setLoadingCepImovel(false);
    }
  };

  const buscarProprietarioCofre = async () => {
    const cleanDoc = formData.imovel.cpfCnpjProprietario.replace(/\D/g, "");
    if (!cleanDoc || !companyInfo?.id) return;

    setIsSearchingProprietario(true);
    try {
      const clientRef = doc(db, "companies", companyInfo.id, "clients", cleanDoc);
      const clientSnap = await getDoc(clientRef);

      if (clientSnap.exists()) {
        const dadosHistorico = clientSnap.data();
        const nomeEncontrado = dadosHistorico.dadosPessoais?.nome || "";
        
        if (nomeEncontrado) {
          setFormData(prev => ({
            ...prev,
            imovel: {
              ...prev.imovel,
              nomeProprietario: nomeEncontrado
            }
          }));
          alert("✓ Histórico encontrado! Nome do proprietário preenchido automaticamente.");
        }
      } else {
        alert("Nenhum histórico encontrado para este CPF/CNPJ.");
      }
    } catch (err) {
      const error = /** @type {Error} */ (err);
      console.error("Erro ao buscar proprietário no cofre:", error.message);
    } finally {
      setIsSearchingProprietario(false);
    }
  };

  /** @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e */
  const handleImovelChange = (e) => {
    let { name, value } = e.target;
    if (name === 'cpfCnpjProprietario') value = maskCpfCnpj(value);
    if (name === 'cep') value = maskCEP(value);
    if (['aluguel', 'condominio', 'iptu', 'agua', 'luz', 'seguroIncendio'].includes(name)) {
      value = maskCurrency(value);
    }
    setFormData((prev) => ({ ...prev, imovel: { ...prev.imovel, [name]: value } }));
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accordionStep, setAccordionStep] = useState(0);
  const [editandoParteId, setEditandoParteId] = useState(null);

  const defaultNovaParte = {
    dadosPessoais: {
       cpf: "", nome: "", docComplementar: "CNH", numDocComplementar: "",
       dataNascimento: "", profissao: "", estadoCivil: "Solteiro(a)",
       pretendenteA: "Locatário",
       morarImovel: false, nacionalidade: "Brasileiro(a)", email: "", telefone: "", observacoes: ""
     },
    perfilFinanceiro: { compoeRenda: true, rendas: /** @type {any[]} */ ([{ id: Date.now(), perfilPor: "Renda Formal", origemRenda: "CLT", valor: "" }]) },
    endereco: { usarCadastrado: false, locatarioSelecionado: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", estado: "", cidade: "" },
    documentos: /** @type {any[]} */ ([])
   };

  const [novaParte, setNovaParte] = useState(defaultNovaParte);

  const abrirModalNovaParte = () => {
    setEditandoParteId(null);
    setNovaParte(defaultNovaParte);
    setAccordionStep(0);
    setIsModalOpen(true);
  };

  /**
   * @param {any} parte
   * @param {number} [targetStep=0]
   */
  const handleEditarParte = (parte, targetStep = 0) => {
    setEditandoParteId(parte.id);
    setNovaParte(parte);
    setAccordionStep(targetStep);
    setIsModalOpen(true);
  };

  /**
   * @param {'dadosPessoais' | 'perfilFinanceiro' | 'endereco'} section
   * @param {string} field
   * @param {any} value
   */
  const handleNovaParteChange = (section, field, value) => {
    let finalValue = value;
    
    if (field === 'cpf') finalValue = maskCpfCnpj(value);
    if (field === 'cep') finalValue = maskCEP(value);
    if (field === 'telefone') finalValue = maskPhone(value);

    setNovaParte(prev => ({
       ...prev,
       [section]: {
         .../** @type {any} */(prev[section]),
         [field]: finalValue
       }
     }));
  };

  /** @param {string} newProfissaoText */
  const handleAddCustomProfissao = async (newProfissaoText) => {
    const trimmedText = newProfissaoText.trim();
    if (!trimmedText) return;

    setCustomProfissoes(prev => [...prev, trimmedText]);
    handleNovaParteChange('dadosPessoais', 'profissao', trimmedText);
    setShowProfissoes(false);

    if (companyInfo?.id) {
      try {
        const companyRef = doc(db, "companies", companyInfo.id);
        await updateDoc(companyRef, {
          customProfissoes: arrayUnion(trimmedText)
        });
      } catch (err) {
        const error = /** @type {Error} */ (err);
        console.error("Erro ao salvar profissão customizada:", error.message);
      }
    }
  };

  const buscarParteCofre = async () => {
    const cleanCpf = novaParte.dadosPessoais.cpf.replace(/\D/g, "");
    if (cleanCpf.length !== 11 || !companyInfo?.id) {
      alert("Digite um CPF com 11 dígitos para buscar o histórico.");
      return;
    }

    setIsSearchingCpf(true);
    try {
      const clientRef = doc(db, "companies", companyInfo.id, "clients", cleanCpf);
      const clientSnap = await getDoc(clientRef);

      if (clientSnap.exists()) {
        const dadosHistorico = clientSnap.data().dadosPessoais;
        
        setNovaParte(prev => ({
          ...prev,
          dadosPessoais: {
             ...prev.dadosPessoais,
             nome: dadosHistorico.nome || "",
            docComplementar: dadosHistorico.docComplementar || "CNH",
            numDocComplementar: dadosHistorico.numDocComplementar || "",
            dataNascimento: dadosHistorico.dataNascimento || "",
            profissao: dadosHistorico.profissao || "",
            estadoCivil: dadosHistorico.estadoCivil || "Solteiro(a)",
            nacionalidade: dadosHistorico.nacionalidade || "Brasileiro(a)",
          }
        }));
        
        alert("✓ Histórico encontrado! Dados pessoais básicos preenchidos automaticamente.");
      } else {
        alert("Nenhum histórico encontrado para este CPF.");
      }
    } catch (err) {
      const error = /** @type {Error} */ (err);
      console.error("Erro ao buscar cliente no cofre:", error.message);
    } finally {
      setIsSearchingCpf(false);
    }
  };

  /**
   * @param {number} id
   * @param {string} field
   * @param {string} value
   */
  const handleRendaChange = (id, field, value) => {
    let finalValue = value;
    if (field === 'valor') finalValue = maskCurrency(value);

    setNovaParte(prev => ({
      ...prev, perfilFinanceiro: {
         ...prev.perfilFinanceiro,
         rendas: prev.perfilFinanceiro.rendas.map(/** @param {any} r */ (r) => {
          if (r.id === id) {
            const updatedRenda = { ...r, [field]: finalValue };
            
            // SMART SWITCH MANTIDO
            if (field === 'perfilPor') {
              updatedRenda.origemRenda = finalValue === 'Renda Formal' 
                ? 'CLT' 
                : 'Renda não declarada';
            }
            
            return updatedRenda;
          }
          return r;
        })
       }
    }));
  };

  const adicionarOutraRenda = () => {
    setNovaParte(prev => ({
      ...prev, perfilFinanceiro: { ...prev.perfilFinanceiro, rendas: [...prev.perfilFinanceiro.rendas, { id: Date.now(), perfilPor: "Renda Formal", origemRenda: "CLT", valor: "" }] }
    }));
  };

  /** @param {number} idToRemove */
  const removerRenda = (idToRemove) => {
    setNovaParte(prev => ({
      ...prev, perfilFinanceiro: { ...prev.perfilFinanceiro, rendas: prev.perfilFinanceiro.rendas.filter(/** @param {any} r */ (r) => r.id !== idToRemove) }
    }));
  };

  const buscarCepParte = async () => {
    const cepNumeros = novaParte.endereco.cep.replace(/\D/g, '');
    if (cepNumeros.length !== 8) return;

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepNumeros}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setNovaParte(prev => ({
          ...prev, endereco: { ...prev.endereco, endereco: data.logradouro, bairro: data.bairro, cidade: data.localidade, estado: data.uf }
        }));
      }
    } catch (err) {
       const error = /** @type {Error} */ (err);
      console.error("Erro na busca de CEP da parte", error.message);
     }
  };

  /** @param {React.ChangeEvent<HTMLInputElement>} e */
  const handleFileUpload = (e) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const novosDocumentos = files.map((f, index) => ({
      id: Date.now() + index,
      nome: f.name,
      tipo: "Documentos Pessoais",
      obs: "",
      status: true,
      rawFile: f
     }));

    setNovaParte(prev => ({
      ...prev,
      documentos: [...prev.documentos, ...novosDocumentos]
    }));
    
    e.target.value = '';
  };

  /** @param {any} fileItem */
  const handlePreviewFile = (fileItem) => {
    const url = fileItem.url || (fileItem.rawFile ? URL.createObjectURL(fileItem.rawFile) : null);
    if (url) window.open(url, '_blank');
  };

  const handleAccordionNext = () => { if (accordionStep < 4) setAccordionStep(prev => prev + 1); };
  const handleAccordionPrev = () => { if (accordionStep > 0) setAccordionStep(prev => prev - 1); };

  const salvarNovaParte = () => {
    if (novaParte.dadosPessoais.nome.trim() === "" || novaParte.dadosPessoais.cpf.trim() === "") {
      alert("Por favor, preencha pelo menos Nome e CPF.");
      return;
    }

    const hasDocPessoal = novaParte.documentos.some(/** @param {any} d */ (d) => d.tipo === "Documentos Pessoais");
    const hasResidencia = novaParte.documentos.some(/** @param {any} d */ (d) => d.tipo === "Comprovante de Residência");
    const hasRenda = novaParte.documentos.some(/** @param {any} d */ (d) => d.tipo === "Comprovante de Renda");

    if (!hasDocPessoal || !hasResidencia || !hasRenda) {
      const missingDocs = [];
      if (!hasDocPessoal) missingDocs.push("Documentos Pessoais");
      if (!hasResidencia) missingDocs.push("Comprovante de Residência");
      if (!hasRenda) missingDocs.push("Comprovante de Renda");
      
      alert(`⚠️ ATENÇÃO: Faltam documentos obrigatórios!\n\nVocê precisa anexar e classificar:\n- ${missingDocs.join('\n- ')}\n\nVá na aba 4 (Documentos) e classifique corretamente antes de salvar.`);
      return;
    }

    setFormData(prev => {
      let novasPartes;
      if (editandoParteId) {
        novasPartes = prev.partes.map(/** @param {any} p */ (p) => p.id === editandoParteId ? { ...novaParte, id: editandoParteId } : p);
      } else {
        novasPartes = [...prev.partes, { ...novaParte, id: Date.now() }];
      }
      return { ...prev, partes: novasPartes };
    });
    
    setIsModalOpen(false);
    setNovaParte(defaultNovaParte);
    setAccordionStep(0);
    setEditandoParteId(null);
  };

  /** @param {number} id */
  const removerParte = (id) => {
    setFormData(prev => ({ ...prev, partes: prev.partes.filter(/** @param {any} p */ (p) => p.id !== id) }));
  };

  const steps = [
    { id: 1, title: "Serviço" }, { id: 2, title: "Imóvel" }, { id: 3, title: "Observações" }, { id: 4, title: "Partes" }
  ];

  const getValidationErrors = () => {
    const errors = [];
    if (!formData.servico) errors.push("Selecione um serviço (Passo 1).");

    const i = formData.imovel;
    if (!i.cpfCnpjProprietario || !i.nomeProprietario || !i.aluguel || !i.tipo || !i.cep || !i.endereco || !i.numero || !i.bairro || !i.estado || !i.cidade) {
      errors.push("Existem campos obrigatórios em branco nos Dados do Imóvel (Passo 2).");
    }

    if (formData.partes.length === 0) {
      errors.push("Cadastre pelo menos uma pessoa na aba de Partes (Passo 4).");
    }
    return errors;
  };

  const handleNext = () => {
     if (canAdvance()) {
      if (currentStep < steps.length) setCurrentStep(prev => prev + 1);
     }
  };

  const handlePrev = () => { if (currentStep > 1) setCurrentStep(prev => prev - 1); else navigate("/dashboard/requests"); };

  const openConfirmModal = () => {
    setShowConfirmModal(true);
  };

  // ============================================================================
  // SUBMISSÃO COM PAYLOAD SANITIZADO E RESTRITO (Fase 3 + LGPD)
  // ============================================================================
  const handleFinish = async () => {
    if (isSaving || !aceiteTermos) return;
    setIsSaving(true);
    setUploadProgress(id ? "Atualizando ficha..." : "Gerando protocolo e processando arquivos...");

    try {
      const companyId = companyInfo?.id || "sem_empresa";
      const realCompanyName = companyInfo?.nome || companyInfo?.razaoSocial || companyInfo?.companyName || "Imobiliária Logada";

      // MAPEAMENTO ESTRITO DO IMÓVEL (Anti-Injeção e Desmascarado)
      const imovelLimpo = {
        codigo: formData.imovel.codigo?.trim() || "",
        cpfCnpjProprietario: unmaskStr(formData.imovel.cpfCnpjProprietario),
        nomeProprietario: formData.imovel.nomeProprietario?.trim() || "",
        aluguel: unmaskCurrency(formData.imovel.aluguel),
        condominio: unmaskCurrency(formData.imovel.condominio),
        iptu: unmaskCurrency(formData.imovel.iptu),
        agua: unmaskCurrency(formData.imovel.agua),
        luz: unmaskCurrency(formData.imovel.luz),
        seguroIncendio: unmaskCurrency(formData.imovel.seguroIncendio),
        tipo: formData.imovel.tipo || "",
        cep: unmaskStr(formData.imovel.cep),
        endereco: formData.imovel.endereco?.trim() || "",
        numero: formData.imovel.numero?.trim() || "",
        complemento: formData.imovel.complemento?.trim() || "",
        bairro: formData.imovel.bairro?.trim() || "",
        estado: formData.imovel.estado?.trim() || "",
        cidade: formData.imovel.cidade?.trim() || ""
      };

      await runTransaction(db, async (transaction) => {
        let finalProtocolo = formData.numeroSolicitacao;
        let requestRef;

        if (id) {
          if (!formData._idBanco) {
            throw new Error("Erro de integridade: O ID do documento foi perdido na memória. Recarregue a página e tente novamente.");
          }
          requestRef = doc(db, "requests", formData._idBanco);

          const novoStatus = Boolean(formData.isLegacy) ? "Legado" : "Em Analise";

            const novoHistorico = {
              data: new Date().toISOString(),
              status: novoStatus,
              mensagem: "Ficha editada/corrigida pela imobiliária",
              autor: userInfo?.name || "Atendente Imobiliária"
            };
            
            const reqSnap = await transaction.get(requestRef);
            let histAtual = reqSnap.exists() ? (reqSnap.data().historico || []) : [];
            histAtual.push(novoHistorico);

            const updatePayload = {
              servico: formData.servico,
              isLegacy: Boolean(formData.isLegacy),
              imovel: imovelLimpo,
              observacoes: formData.observacoes?.trim() || "",
              taxaAtivacao: unmaskCurrency(formData.taxaAtivacao),
              status: novoStatus,
              updatedAt: serverTimestamp(),
              historico: histAtual
            };

            transaction.update(requestRef, updatePayload);

          } else {
            const counterRef = doc(db, "system_counters", "requests");
            const counterSnap = await transaction.get(counterRef);
            
            const currentYear = new Date().getFullYear();
            let nextNumber = 1;

            if (counterSnap.exists()) {
              const counterData = counterSnap.data();
              if (counterData.year === currentYear) {
                nextNumber = (counterData.lastNumber || 0) + 1;
              }
            }

            finalProtocolo = `${nextNumber.toString().padStart(4, '0')}/${currentYear}`;
            transaction.set(counterRef, { lastNumber: nextNumber, year: currentYear }, { merge: true });

            requestRef = doc(collection(db, "requests"));
            
            const initialStatus = Boolean(formData.isLegacy) ? "Legado" : "Em Analise";

            const requestPayload = {
              protocolo: finalProtocolo,
              numeroSolicitacao: finalProtocolo,
              dataSolicitacaoTexto: formData.dataSolicitacao,
              servico: formData.servico,
              isLegacy: Boolean(formData.isLegacy),
              imovel: imovelLimpo,
              observacoes: formData.observacoes?.trim() || "",
              taxaAtivacao: unmaskCurrency(formData.taxaAtivacao),
              companyId: companyId,
              companyName: realCompanyName,
              status: initialStatus,
              createdByUid: currentUser?.uid,
              createdByName: userInfo?.name || "Atendente",
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              // TRILHA DE CONSENTIMENTO LGPD
              consentimento: {
                aceiteTermos: aceiteTermos,
                ip: clientIp || "IP_NAO_CAPTURADO",
                timestamp: new Date().toISOString()
              }
            };

          transaction.set(requestRef, requestPayload);
        }

        // Salvamento no Cofre de Proprietário
        if (imovelLimpo.cpfCnpjProprietario && imovelLimpo.nomeProprietario && companyId !== "sem_empresa") {
          const cleanDocProp = imovelLimpo.cpfCnpjProprietario;
          if (cleanDocProp.length >= 11) {
             const propVaultRef = doc(db, "companies", companyId, "clients", cleanDocProp);
            transaction.set(propVaultRef, {
              dadosPessoais: {
                cpf: imovelLimpo.cpfCnpjProprietario,
                nome: imovelLimpo.nomeProprietario,
              },
              lastUpdated: serverTimestamp()
            }, { merge: true });
           }
        }

        for (const parte of formData.partes) {
          const idDaParteString = parte.id.toString(); 
          const parteRef = doc(db, `requests/${requestRef.id}/partes`, idDaParteString);
          
          const docsComLinks = [];
          
          for (const docFile of parte.documentos) {
            if (docFile.rawFile) {
              setUploadProgress(`Enviando: ${docFile.nome}...`);
              const cleanProtocolo = finalProtocolo.replace('/', '-');
              const fileRef = ref(
                storage,
                `companiesDocs/${storageEnvironment}/${companyId}/${cleanProtocolo}/${unmaskStr(parte.dadosPessoais.cpf)}_${docFile.nome}`
              );
              await uploadBytes(fileRef, docFile.rawFile);
              const url = await getDownloadURL(fileRef);
              docsComLinks.push({
                nome: docFile.nome, tipo: docFile.tipo, obs: docFile.obs, url: url, status: docFile.status
              });
            } else if (docFile.url) {
              docsComLinks.push(docFile);
            }
          }

          // MAPEAMENTO ESTRITO DAS PARTES (Anti-Injeção e Desmascarado)
          const partePayload = {
            id: parteRef.id,
            dadosPessoais: {
               cpf: unmaskStr(parte.dadosPessoais.cpf),
               nome: parte.dadosPessoais.nome?.trim() || "",
               docComplementar: parte.dadosPessoais.docComplementar || "",
               numDocComplementar: parte.dadosPessoais.numDocComplementar?.trim() || "",
               dataNascimento: parte.dadosPessoais.dataNascimento || "",
               profissao: parte.dadosPessoais.profissao?.trim() || "",
               estadoCivil: parte.dadosPessoais.estadoCivil || "",
               pretendenteA: parte.dadosPessoais.pretendenteA || "Locatário",
               morarImovel: Boolean(parte.dadosPessoais.morarImovel),
               nacionalidade: parte.dadosPessoais.nacionalidade?.trim() || "",
               email: parte.dadosPessoais.email?.trim().toLowerCase() || "",
               telefone: unmaskStr(parte.dadosPessoais.telefone),
               observacoes: parte.dadosPessoais.observacoes?.trim() || ""
            },
            perfilFinanceiro: {
               compoeRenda: Boolean(parte.perfilFinanceiro.compoeRenda),
               rendas: parte.perfilFinanceiro.rendas.map(/** @param {any} r */ r => ({
                   id: r.id || Date.now(),
                   perfilPor: r.perfilPor || "",
                   origemRenda: r.origemRenda || "",
                   valor: unmaskCurrency(r.valor)
               }))
            },
            endereco: {
               usarCadastrado: Boolean(parte.endereco.usarCadastrado),
               locatarioSelecionado: parte.endereco.locatarioSelecionado || "",
               cep: unmaskStr(parte.endereco.cep),
               endereco: parte.endereco.endereco?.trim() || "",
               numero: parte.endereco.numero?.trim() || "",
               complemento: parte.endereco.complemento?.trim() || "",
               bairro: parte.endereco.bairro?.trim() || "",
               estado: parte.endereco.estado?.trim() || "",
               cidade: parte.endereco.cidade?.trim() || ""
            },
            documentos: docsComLinks
          };

          transaction.set(parteRef, partePayload);

          if (partePayload.dadosPessoais.cpf) {
            const cleanCpf = partePayload.dadosPessoais.cpf;
            if (cleanCpf.length === 11 && companyId !== "sem_empresa") {
              const clientVaultRef = doc(db, "companies", companyId, "clients", cleanCpf);
              transaction.set(clientVaultRef, {
                dadosPessoais: partePayload.dadosPessoais,
                perfilFinanceiro: partePayload.perfilFinanceiro,
                endereco: partePayload.endereco,
                lastUpdated: serverTimestamp()
              }, { merge: true });
            }
          }
        }
      });

      setShowConfirmModal(false);
      navigate("/dashboard/requests");
      
    } catch (err) {
      const error = /** @type {Error} */ (err);
      console.error("⚠️ Erro no processamento:", error.message);
      alert(error.message.includes("memória") ? error.message : "Ocorreu um erro ao salvar a ficha. Tente novamente.");
    } finally {
      setIsSaving(false);
      setUploadProgress("");
    }
  };

  const validationErrors = getValidationErrors();
  const isValid = validationErrors.length === 0;

  /** @param {boolean} isValidStatus */
  const getIconClass = (isValidStatus) => isValidStatus 
    ? "w-7 h-7 rounded-full border border-brand-primary text-brand-primary flex items-center justify-center bg-brand-primary/10 transition-colors"
    : "w-7 h-7 rounded-full border border-red-500 text-red-500 flex items-center justify-center bg-red-500/10 transition-colors";

  if (isLoadingEdicao) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in">
        <Loader2 className="w-12 h-12 text-brand-primary animate-spin mb-4" />
        <p className="text-slate-500 font-medium">Carregando ficha para edição...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fade-in bg-slate-50 min-h-full font-sans">
      
      {/* HEADER BREADCRUMB */}
      <div className="mb-8">
        <div className="flex items-center text-sm text-slate-500 mb-2">
          <Link to="/dashboard/requests" className="hover:text-brand-primary transition-colors font-medium">Lista de Solicitações</Link>
          <ChevronRight className="w-4 h-4 mx-1" />
          <span className="font-bold text-slate-800">
            {id ? `Editando Request ${formData.numeroSolicitacao}` : 'Adicionar Solicitação'}
          </span>
        </div>
      </div>

      {/* BANNER DE PENDÊNCIA (SÓ NO MODO EDIÇÃO) */}
      {id && ultimoParecer && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3 shadow-sm mb-6">
          <AlertCircle className="w-6 h-6 text-yellow-600 mt-0.5 shrink-0" />
          <div>
            <h3 className="font-bold text-yellow-800">Motivo da Pendência:</h3>
            <p className="text-sm text-yellow-700 mt-1 italic leading-relaxed">"{ultimoParecer}"</p>
            <p className="text-xs text-yellow-600 mt-2 font-bold">Corrija os dados abaixo e clique em Concluir para reenviar a ficha.</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col w-full min-h-[600px] relative">
        
        {/* CABEÇALHO DO WIZARD COM STEPS INTERATIVOS */}
        <div className="p-6 sm:p-8 border-b border-slate-200 flex flex-col lg:flex-row items-center justify-between gap-6 relative z-0 bg-white rounded-t-2xl">
          <button type="button" onClick={handlePrev} className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded-full hover:bg-slate-100 transition-colors shadow-sm">
            <ChevronLeft className="w-4 h-4" /> Voltar
          </button>
          
          <div className="flex-1 w-full max-w-2xl flex items-center justify-between relative">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-[2px] bg-slate-200 -z-10"></div>
            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-brand-primary transition-all duration-300 -z-10" style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}></div>

            {steps.map((step) => {
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              const canClick = !!formData.servico || step.id === 1;

              return (
                <div key={step.id} className="flex flex-col items-center bg-white px-2 relative z-10">
                  <button 
                    type="button"
                    onClick={() => canClick && setCurrentStep(step.id)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-300 focus:outline-none ${isActive ? 'border-brand-primary bg-brand-primary text-white shadow-md shadow-brand-primary/30 scale-110' : isCompleted ? 'border-brand-primary bg-brand-primary text-white hover:scale-110' : 'border-slate-200 bg-slate-50 text-slate-400 hover:border-brand-primary/50'} ${canClick ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                    title={canClick ? `Ir para a etapa: ${step.title}` : "Selecione o serviço primeiro para desbloquear as etapas"}
                  >
                    {isCompleted ? <Check className="w-4 h-4" /> : step.id}
                  </button>
                  <button 
                    type="button"
                    onClick={() => canClick && setCurrentStep(step.id)}
                    className={`text-[11px] mt-2 font-bold transition-colors duration-300 focus:outline-none ${isActive || isCompleted ? 'text-slate-800' : 'text-slate-400'} ${canClick ? 'cursor-pointer hover:text-brand-primary' : 'cursor-not-allowed opacity-50'}`}
                  >
                    {step.title}
                  </button>
                </div>
              );
            })}
          </div>

          <button 
            type="button"
            onClick={currentStep === steps.length ? openConfirmModal : handleNext}
            disabled={!canAdvance()}
            className={`flex items-center gap-2 px-6 py-2 text-sm font-bold rounded-full transition-all z-10 shadow-md ${canAdvance() ? 'bg-brand-primary text-white hover:bg-teal-600 shadow-brand-primary/30' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
          >
            {currentStep === steps.length ? (id ? "Reenviar Edição" : "Concluir") : <>Avançar <ChevronRight className="w-4 h-4" /></>}
          </button>
        </div>

        {/* INFO FIXA AUTOMATIZADA */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><p className="text-xs font-semibold text-slate-500 mb-0.5">Nº Solicitação</p><p className="text-sm font-bold text-slate-800">{formData.numeroSolicitacao}</p></div>
          <div><p className="text-xs font-semibold text-slate-500 mb-0.5">Data Solicitação</p><p className="text-sm font-bold text-slate-800">{formData.dataSolicitacao}</p></div>
          <div><p className="text-xs font-semibold text-slate-500 mb-0.5">Situação</p><p className="text-sm font-bold text-slate-800">{id ? "Correção Pendente" : "Em cadastramento"}</p></div>
          <div><p className="text-xs font-semibold text-slate-500 mb-0.5">Atendente</p><p className="text-sm font-bold text-slate-800">{userInfo?.name || "Atendente"}</p></div>
        </div>

        <div className="p-6 sm:p-8 flex-1">
          {/* PASSO 1: SERVIÇO */}
          {currentStep === 1 && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold text-brand-primary mb-2">Escolha o serviço</h2>
              <p className="text-sm text-slate-500 mb-8 font-medium">Favor escolher o serviço desejado para esta locação.</p>

              <button 
                type="button" 
                onClick={() => setFormData(prev => ({...prev, servico: "fianca_mais"}))} 
                className={`text-left cursor-pointer w-full max-w-sm rounded-2xl border-2 transition-all overflow-hidden focus:outline-none ${formData.servico === "fianca_mais" ? "border-brand-primary ring-4 ring-brand-primary/10 shadow-lg" : "border-slate-200 hover:border-brand-primary/50"}`}
              >
                <div className="bg-slate-50 h-28 flex items-center justify-center relative border-b border-slate-200">
                  {formData.servico === "fianca_mais" && <div className="absolute top-3 right-3 bg-brand-primary rounded-full p-1"><Check className="w-4 h-4 text-white" /></div>}
                  <span className="text-slate-800 font-bold text-2xl tracking-wide flex items-center"><span className="text-brand-primary mr-1 font-bold"> </span>fiança<span className="font-light">MAIS</span></span>
                </div>
                <div className="p-5 bg-white h-full"><p className="text-sm text-slate-500 leading-relaxed font-medium">Fiança Locatícia, Clube de Benefícios, Assistência Residencial e Assistência Jurídica.</p></div>
              </button>

              {/* NOVA CHAVE SELETORA DE CADASTRO LEGADO (FASE 5) */}
              <div className="mt-8 p-6 bg-slate-50 border border-slate-200 rounded-2xl shadow-sm max-w-xl">
                <label className="flex items-start justify-between cursor-pointer">
                  <div className="pr-4">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      Migração de Contrato Legado
                    </h3>
                    <p className="text-xs text-slate-500 mt-2 font-medium leading-relaxed">
                      Ative esta opção para importar contratos antigos e evitar custos redundantes de bureau. 
                      O sistema fará o bypass automático da análise de crédito (FC) e assinaturas (ClickSign), 
                      ejetando este request diretamente no motor de faturamento do Asaas.
                    </p>
                  </div>
                  <div className="shrink-0 mt-1">
                    <CustomToggle 
                      checked={formData.isLegacy} 
                      onChange={(val) => setFormData(prev => ({...prev, isLegacy: val}))} 
                    />
                  </div>
                </label>
              </div>

            </div>
          )}

          {/* PASSO 2: IMÓVEL */}
          {currentStep === 2 && (
            <div className="animate-fade-in max-w-5xl">
              <h2 className="text-2xl font-bold text-brand-primary mb-2">Dados do imóvel</h2>
              <p className="text-sm text-slate-500 mb-8 font-medium">Preencha as informações do imóvel pretendido à locação.</p>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Código do Imóvel</label><input type="text" name="codigo" value={formData.imovel.codigo} onChange={handleImovelChange} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary shadow-sm" /></div>
                  
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">CPF/CNPJ Proprietário *</label>
                    <div className="flex gap-2">
                      <input type="text" name="cpfCnpjProprietario" value={formData.imovel.cpfCnpjProprietario} onChange={handleImovelChange} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary shadow-sm" />
                      <button type="button" onClick={(e) => { e.preventDefault(); buscarProprietarioCofre(); }} disabled={isSearchingProprietario} className="px-3 bg-brand-primary/10 text-brand-primary border border-brand-primary/20 hover:bg-brand-primary hover:text-white rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center shadow-sm" title="Buscar proprietário salvo">
                        {isSearchingProprietario ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Nome Proprietário *</label><input type="text" name="nomeProprietario" value={formData.imovel.nomeProprietario} onChange={handleImovelChange} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary shadow-sm" /></div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  {['aluguel', 'condominio', 'iptu', 'agua', 'luz', 'seguroIncendio'].map((field) => (
                    <div key={field}>
                      <label className="flex items-center text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                        R$ {field === 'seguroIncendio' ? 'Seg. Incêndio' : field} {field === 'aluguel' && '*'}
                        {(field === 'agua' || field === 'luz') && (
                          <div className="group relative flex items-center ml-1 cursor-help" title={field === 'agua' ? "Consumo sugerido: R$ 90,00/pessoa." : "Consumo sugerido: R$ 110,00/pessoa."}>
                            <Info className="w-3.5 h-3.5 text-brand-primary opacity-80" />
                          </div>
                        )}
                      </label>
                      {/* CAST APLICADO AQUI: formData.imovel indexado de forma segura */}
                      <input type="text" name={field} value={/** @type {any} */(formData.imovel)[field]} onChange={handleImovelChange} placeholder="0,00" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary shadow-sm" />
                    </div>
                  ))}
                </div>

                <hr className="border-slate-100" />

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-3"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Tipo de Imóvel *</label><select name="tipo" value={formData.imovel.tipo} onChange={handleImovelChange} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary shadow-sm cursor-pointer"><option value="">Selecione...</option><option value="apartamento">Apartamento</option><option value="casa">Casa</option><option value="comercial">Comercial</option></select></div>
                  <div className="md:col-span-3">
                    <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">CEP *</label>
                    <div className="flex gap-2">
                      <input type="text" name="cep" value={formData.imovel.cep} onChange={handleImovelChange} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary shadow-sm" />
                      <button type="button" onClick={(e) => { e.preventDefault(); buscarCepImovel(); }} disabled={loadingCepImovel} className="px-3 bg-brand-primary/10 text-brand-primary border border-brand-primary/20 hover:bg-brand-primary hover:text-white rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center shadow-sm">
                        {loadingCepImovel ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="md:col-span-6"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Endereço *</label><input type="text" name="endereco" value={formData.imovel.endereco} onChange={handleImovelChange} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary shadow-sm" /></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-2"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Número *</label><input type="text" name="numero" value={formData.imovel.numero} onChange={handleImovelChange} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary shadow-sm" /></div>
                  <div className="md:col-span-3"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Complemento</label><input type="text" name="complemento" value={formData.imovel.complemento} onChange={handleImovelChange} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary shadow-sm" /></div>
                  <div className="md:col-span-3"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Bairro *</label><input type="text" name="bairro" value={formData.imovel.bairro} onChange={handleImovelChange} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary shadow-sm" /></div>
                  <div className="md:col-span-1"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">UF *</label><input type="text" name="estado" value={formData.imovel.estado} onChange={handleImovelChange} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary shadow-sm" /></div>
                  <div className="md:col-span-3"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Cidade *</label><input type="text" name="cidade" value={formData.imovel.cidade} onChange={handleImovelChange} className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary shadow-sm" /></div>
                </div>
              </div>
            </div>
          )}

          {/* PASSO 3: OBSERVAÇÕES E TAXAS */}
          {currentStep === 3 && (
            <div className="animate-fade-in max-w-4xl">
              <h2 className="text-2xl font-bold text-brand-primary mb-2">Observações e Taxas</h2>
              <p className="text-sm text-slate-500 mb-8 font-medium">Informações adicionais e valores acordados para a efetivação da garantia.</p>
              
              <div className="mb-8 p-6 bg-teal-50 border border-teal-200 rounded-2xl shadow-sm">
                <label className="block text-sm font-bold text-teal-800 mb-3">
                  Comissão da Imobiliária / Taxa de Setup (Taxa única)
                </label>
                
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {VALORES_COMISSAO.map((valor) => (
                    <button
                      key={valor}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, taxaAtivacao: valor }))}
                      className={`py-2 px-3 rounded-xl font-bold text-sm transition-all border shadow-sm ${
                        formData.taxaAtivacao === valor
                          ? 'bg-brand-primary text-white border-brand-primary ring-2 ring-brand-primary/20 scale-105 shadow-md'
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      R$ {valor}
                    </button>
                  ))}
                </div>
                
                <p className="text-xs text-teal-600 mt-4 font-bold flex items-center gap-1.5">
                  <Info className="w-4 h-4 shrink-0" />
                  Selecione o valor que a imobiliária receberá de comissão (setup). Este valor será cobrado do inquilino como taxa única no primeiro faturamento.
                </p>
              </div>

              <label className="block text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Observações da Locação</label>
              <textarea name="observacoes" value={formData.observacoes} onChange={(e) => setFormData(prev => ({...prev, observacoes: e.target.value}))} rows={5} placeholder="Digite suas observações aqui (opcional)..." className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:outline-none focus:border-brand-primary shadow-inner resize-none"></textarea>
            </div>
          )}

          {/* PASSO 4: PARTES */}
          {currentStep === 4 && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold text-brand-primary mb-2">Partes</h2>
              <p className="text-sm text-slate-500 mb-6 font-medium">Adicione as pessoas que farão parte da locação.</p>
              
              <div className="mb-6">
                <button type="button" onClick={abrirModalNovaParte} className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-brand-primary hover:bg-teal-600 rounded-xl transition-all shadow-md shadow-brand-primary/20">
                  <Plus className="w-4 h-4" /> Adicionar Parte
                </button>
              </div>

              <div className="overflow-x-auto border border-slate-200 rounded-2xl shadow-sm">
                <table className="w-full text-left border-collapse min-w-[600px] bg-white">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500">
                      <th className="p-4 whitespace-nowrap">Nome completo</th>
                      <th className="p-4 whitespace-nowrap">Pretendente a</th>
                      <th className="p-4 whitespace-nowrap">CPF</th>
                      <th className="p-4 text-center whitespace-nowrap">Documentos</th>
                      <th className="p-4 text-center whitespace-nowrap">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {formData.partes.length === 0 ? (
                      <tr><td colSpan={5} className="p-6 text-center text-sm text-slate-400 font-medium">Nenhum dado encontrado.</td></tr>
                    ) : (
                      formData.partes.map((/** @type {any} */ parte) => (
                        <tr key={parte.id} className="hover:bg-slate-50 transition-colors">
                          <td className="p-4 text-sm font-bold text-slate-800 whitespace-nowrap truncate max-w-[200px]" title={parte.dadosPessoais.nome}>{parte.dadosPessoais.nome}</td>
                          <td className="p-4 text-sm font-semibold text-slate-600 whitespace-nowrap">{parte.dadosPessoais.pretendenteA}</td>
                          <td className="p-4 text-sm font-semibold text-slate-600 whitespace-nowrap">{parte.dadosPessoais.cpf}</td>
                          
                          <td className="p-4 text-center">
                            {(() => {
                              /** @param {any[]} docs @param {string} tipo */
                              const checkStatus = (docs, tipo) => {
                                const typeDocs = docs?.filter(/** @param {any} d */ (d) => d.tipo === tipo) || [];
                                if (typeDocs.length === 0) return false;
                                return typeDocs.every(/** @param {any} d */ (d) => d.status !== false);
                              };

                              const validDocPessoal = checkStatus(parte.documentos, "Documentos Pessoais");
                              const validResidencia = checkStatus(parte.documentos, "Comprovante de Residência");
                              const validRenda = checkStatus(parte.documentos, "Comprovante de Renda");

                              return (
                                <div className="flex justify-center gap-2">
                                  <button type="button" onClick={() => handleEditarParte(parte, 4)} className="focus:outline-none hover:scale-110 transition-transform">
                                    <div className={getIconClass(validDocPessoal)} title={validDocPessoal ? "Documentos Pessoais (Ok)" : "Documentos Pessoais (Pendente)"}>
                                      <Contact className="w-4 h-4" />
                                    </div>
                                  </button>
                                  <button type="button" onClick={() => handleEditarParte(parte, 4)} className="focus:outline-none hover:scale-110 transition-transform">
                                    <div className={getIconClass(validResidencia)} title={validResidencia ? "Comprovante de Residência (Ok)" : "Comprovante de Residência (Pendente)"}>
                                      <Home className="w-4 h-4" />
                                    </div>
                                  </button>
                                  <button type="button" onClick={() => handleEditarParte(parte, 4)} className="focus:outline-none hover:scale-110 transition-transform">
                                    <div className={getIconClass(validRenda)} title={validRenda ? "Comprovante de Renda (Ok)" : "Comprovante de Renda (Pendente)"}>
                                      <DollarSign className="w-4 h-4" />
                                    </div>
                                  </button>
                                </div>
                              );
                            })()}
                          </td>

                          <td className="p-4 text-center">
                            <div className="flex justify-center gap-1">
                              <button type="button" onClick={() => handleEditarParte(parte)} className="p-2 text-slate-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors" title="Editar">
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button type="button" onClick={() => removerParte(parte.id)} className="p-2 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors" title="Remover">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {formData.partes.length === 0 && <p className="text-xs text-red-500 mt-3 font-bold flex items-center gap-1">* Obrigatório informar as pessoas.</p>}
            </div>
          )}
        </div>
      </div>

      {/* MODAL DE ADICIONAR/EDITAR PARTE */}
      {isModalOpen && (
        <SafePortal>
          <div 
            className="flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4 sm:p-6"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', zIndex: 999999 }}
          >
            <div className="relative bg-white w-full max-w-5xl flex flex-col rounded-2xl shadow-2xl border border-slate-200" style={{ maxHeight: '90vh' }}>
              
              <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-200 bg-white shrink-0 rounded-t-2xl">
                <h2 className="text-xl font-bold text-brand-primary">Parte {editandoParteId && "- Editando"}</h2>
                <button type="button" onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1.5 rounded-full bg-slate-50 border border-slate-200 transition-colors"><X className="w-5 h-5" /></button>
              </div>

              <div className="p-5 sm:p-6 overflow-y-auto custom-scrollbar flex-1 min-h-0 space-y-4 bg-slate-50">
                 
                {/* ACORDEÃO 1: DADOS PESSOAIS */}
                <div className="border border-slate-200 rounded-xl bg-white shadow-sm transition-all">
                  <button type="button" onClick={() => setAccordionStep(1)} className={`w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-slate-50 transition-colors focus:outline-none ${accordionStep === 1 ? 'rounded-t-xl' : 'rounded-xl'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${accordionStep === 1 ? 'bg-brand-primary' : 'bg-slate-300'}`}>1</div>
                      <span className="font-bold text-slate-800">Dados pessoais</span>
                    </div>
                    {accordionStep === 1 ? <ChevronUp className="w-5 h-5 text-brand-primary" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </button>
                  
                  {accordionStep === 1 && (
                    <div className="px-5 pb-6 pt-2 border-t border-slate-100 space-y-8 animate-fade-in rounded-b-xl">
                      
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-4">
                        <div className="md:col-span-3">
                          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">CPF*</label>
                          <div className="flex gap-2">
                            <input 
                              type="text" 
                              value={novaParte.dadosPessoais.cpf} 
                              onChange={(e) => handleNovaParteChange('dadosPessoais', 'cpf', e.target.value)} 
                              className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 transition-colors" 
                            />
                            <button type="button" onClick={(e) => { e.preventDefault(); buscarParteCofre(); }} disabled={isSearchingCpf} className="px-3 bg-brand-primary/10 text-brand-primary border border-brand-primary/20 hover:bg-brand-primary hover:text-white rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center py-1 shadow-sm" title="Buscar CPF salvo">
                              {isSearchingCpf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="md:col-span-4"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Nome completo</label><input type="text" value={novaParte.dadosPessoais.nome} onChange={(e) => handleNovaParteChange('dadosPessoais', 'nome', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 transition-colors" /></div>
                        <div className="md:col-span-2"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Documento complementar</label><select value={novaParte.dadosPessoais.docComplementar} onChange={(e) => handleNovaParteChange('dadosPessoais', 'docComplementar', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 cursor-pointer"><option>CNH</option><option>RG</option></select></div>
                        <div className="md:col-span-3"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Número doc. complementar</label><input type="text" value={novaParte.dadosPessoais.numDocComplementar} onChange={(e) => handleNovaParteChange('dadosPessoais', 'numDocComplementar', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 transition-colors" /></div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                        <div className="md:col-span-3">
                          <label className="flex items-center text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                            Data de nascimento
                            {novaParte.dadosPessoais.dataNascimento && calcularIdade(novaParte.dadosPessoais.dataNascimento) !== null && (
                              <span className="ml-2 px-1.5 py-0.5 bg-brand-primary/10 text-brand-primary rounded text-[10px] font-bold">
                                {calcularIdade(novaParte.dadosPessoais.dataNascimento)} anos
                              </span>
                            )}
                          </label>
                          <input type="date" value={novaParte.dadosPessoais.dataNascimento} onChange={(e) => handleNovaParteChange('dadosPessoais', 'dataNascimento', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 cursor-pointer" />
                        </div>
                        
                        {/* PROFISSÃO AUTOCOMPLETE */}
                        <div className="md:col-span-3 relative">
                          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Profissão</label>
                          <input 
                            type="text" 
                            value={novaParte.dadosPessoais.profissao} 
                            onChange={(e) => {
                              handleNovaParteChange('dadosPessoais', 'profissao', e.target.value);
                              setShowProfissoes(true);
                            }}
                            onFocus={() => setShowProfissoes(true)}
                            onBlur={() => setTimeout(() => setShowProfissoes(false), 200)}
                            className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 transition-colors" 
                          />
                          {showProfissoes && novaParte.dadosPessoais.profissao.length > 0 && (
                            <ul className="absolute z-[100] w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-2xl max-h-48 overflow-y-auto custom-scrollbar">
                              {(() => {
                                const valorDigitado = novaParte.dadosPessoais.profissao.toLowerCase();
                                const todasProfissoesCombidadas = [...PROFISSOES_CBO, ...customProfissoes];
                                const profissoesFiltradas = todasProfissoesCombidadas.filter(p => p.toLowerCase().includes(valorDigitado));
                                const exactMatchExists = todasProfissoesCombidadas.some(p => p.toLowerCase() === valorDigitado.trim());

                                return (
                                  <>
                                    {profissoesFiltradas.map((p, idx) => (
                                      <li key={idx} className="border-b border-slate-100 last:border-0">
                                        <button 
                                          type="button"
                                          onClick={() => { 
                                            handleNovaParteChange('dadosPessoais', 'profissao', p);
                                            setShowProfissoes(false);
                                          }}
                                          className="w-full text-left px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-brand-primary/10 hover:text-brand-primary cursor-pointer transition-colors focus:outline-none"
                                        >
                                          {p}
                                        </button>
                                      </li>
                                    ))}
                                    {!exactMatchExists && (
                                      <li className="border-t border-teal-100">
                                        <button 
                                          type="button"
                                          onClick={() => handleAddCustomProfissao(novaParte.dadosPessoais.profissao)}
                                          className="w-full text-left px-4 py-3 text-sm font-bold text-brand-primary bg-teal-50 hover:bg-teal-100 cursor-pointer transition-colors flex items-center gap-2 focus:outline-none"
                                        >
                                          <Sparkles className="w-4 h-4" /> Adicionar "{novaParte.dadosPessoais.profissao.trim()}"
                                        </button>
                                      </li>
                                    )}
                                  </>
                                );
                              })()}
                            </ul>
                          )}
                        </div>

                        <div className="md:col-span-3">
                          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Estado civil</label>
                          <select value={novaParte.dadosPessoais.estadoCivil} onChange={(e) => handleNovaParteChange('dadosPessoais', 'estadoCivil', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 cursor-pointer">
                            <option>União Estável</option><option>Solteiro(a)</option><option>Casado(a)</option><option>Divorciado(a)</option><option>Separado(a)</option><option>Viúvo(a)</option>
                          </select>
                        </div>
                        
                        <div className="md:col-span-3 flex flex-col justify-end">
                          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Pretendente a</label>
                          <select 
                            value={novaParte.dadosPessoais.pretendenteA} 
                            onChange={(e) => handleNovaParteChange('dadosPessoais', 'pretendenteA', e.target.value)} 
                            className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 mb-4 cursor-pointer"
                          >
                            <option value="Locatário">Locatário</option>
                            <option value="Cônjuge do locatário">Cônjuge do locatário</option>
                          </select>
                          <CustomToggle checked={novaParte.dadosPessoais.morarImovel} onChange={(/** @type {boolean} */ val) => handleNovaParteChange('dadosPessoais', 'morarImovel', val)} label="Vai morar no imóvel" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                        <div className="md:col-span-3"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Nacionalidade</label><input type="text" value={novaParte.dadosPessoais.nacionalidade} onChange={(e) => handleNovaParteChange('dadosPessoais', 'nacionalidade', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 transition-colors" /></div>
                        <div className="md:col-span-3"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">E-mail</label><input type="email" value={novaParte.dadosPessoais.email} onChange={(e) => handleNovaParteChange('dadosPessoais', 'email', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 transition-colors" /></div>
                        <div className="md:col-span-3"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Telefone</label><input type="text" value={novaParte.dadosPessoais.telefone} onChange={(e) => handleNovaParteChange('dadosPessoais', 'telefone', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 transition-colors" placeholder="(00) 00000-0000" /></div>
                        <div className="md:col-span-3"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Observações da Parte</label><input type="text" value={novaParte.dadosPessoais.observacoes} onChange={(e) => handleNovaParteChange('dadosPessoais', 'observacoes', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 transition-colors" /></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ACORDEÃO 2: PERFIL FINANCEIRO */}
                <div className="border border-slate-200 rounded-xl bg-white shadow-sm transition-all">
                  <button type="button" onClick={() => setAccordionStep(2)} className={`w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-slate-50 transition-colors focus:outline-none ${accordionStep === 2 ? 'rounded-t-xl' : 'rounded-xl'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${accordionStep === 2 ? 'bg-brand-primary' : 'bg-slate-300'}`}>2</div>
                      <span className="font-bold text-slate-800">Perfil financeiro</span>
                    </div>
                    {accordionStep === 2 ? <ChevronUp className="w-5 h-5 text-brand-primary" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </button>
                  
                  {accordionStep === 2 && (
                    <div className="px-5 pb-6 pt-4 border-t border-slate-100 space-y-6 animate-fade-in rounded-b-xl">
                      <CustomToggle checked={novaParte.perfilFinanceiro.compoeRenda} onChange={(/** @type {boolean} */ val) => handleNovaParteChange('perfilFinanceiro', 'compoeRenda', val)} label="Compõe renda" />
                      
                      <div className={`space-y-6 transition-opacity duration-300 ${!novaParte.perfilFinanceiro.compoeRenda ? 'opacity-40 pointer-events-none grayscale-[50%]' : ''}`}>
                        {novaParte.perfilFinanceiro.rendas.map((/** @type {any} */ renda, index) => (
                          <div key={renda.id} className="relative bg-slate-50 p-4 rounded-xl border border-slate-200 animate-fade-in">
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                              
                              <div className="md:col-span-4">
                                <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Perfil financeiro por:</label>
                                <select 
                                  disabled={!novaParte.perfilFinanceiro.compoeRenda}
                                  value={renda.perfilPor} 
                                  onChange={(e) => handleRendaChange(renda.id, 'perfilPor', e.target.value)} 
                                  className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                                >
                                  <option value="Renda Formal">Renda Formal</option>
                                  <option value="Renda Informal">Renda Informal</option>
                                </select>
                              </div>

                              <div className="md:col-span-4">
                                <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Origem renda {index === 0 ? 'principal' : 'extra'}</label>
                                <select 
                                  disabled={!novaParte.perfilFinanceiro.compoeRenda}
                                  value={renda.origemRenda} 
                                  onChange={(e) => handleRendaChange(renda.id, 'origemRenda', e.target.value)} 
                                  className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                                >
                                  {renda.perfilPor === 'Renda Formal' ? (
                                    <>
                                      <option value="CLT">CLT</option>
                                      <option value="Funcionário público concursado">Funcionário público concursado</option>
                                      <option value="Funcionário público contratado">Funcionário público contratado</option>
                                      <option value="Aposentado ou Pensionista">Aposentado ou Pensionista</option>
                                      <option value="Pensão judicial">Pensão judicial</option>
                                      <option value="Estagiário ou Bolsista">Estagiário ou Bolsista</option>
                                    </>
                                  ) : (
                                    <>
                                      <option value="Renda não declarada">Renda não declarada</option>
                                      <option value="Empresário">Empresário</option>
                                      <option value="Autônomo">Autônomo</option>
                                      <option value="Aluguel">Aluguel</option>
                                      <option value="Limite de cartão de crédito">Limite de cartão de crédito</option>
                                    </>
                                  )}
                                </select>
                              </div>

                              <div className={`md:col-span-3 ${index > 0 ? 'md:col-span-3' : 'md:col-span-4'}`}>
                                <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">R$ Valor</label>
                                <input disabled={!novaParte.perfilFinanceiro.compoeRenda} type="text" placeholder="0,00" value={renda.valor} onChange={(e) => handleRendaChange(renda.id, 'valor', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 disabled:opacity-70 disabled:cursor-not-allowed" />
                              </div>

                              {index > 0 && (
                                <div className="md:col-span-1 flex justify-end pb-1.5">
                                  <button type="button" disabled={!novaParte.perfilFinanceiro.compoeRenda} onClick={() => removerRenda(renda.id)} className="p-2 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors disabled:cursor-not-allowed" title="Remover Renda">
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        <button type="button" onClick={adicionarOutraRenda} disabled={!novaParte.perfilFinanceiro.compoeRenda} className="text-sm font-bold text-brand-primary hover:text-teal-600 transition-colors mt-2 disabled:cursor-not-allowed focus:outline-none">
                          + Adicionar outra renda
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* ACORDEÃO 3: ENDEREÇO */}
                <div className="border border-slate-200 rounded-xl bg-white shadow-sm transition-all">
                  <button type="button" onClick={() => setAccordionStep(3)} className={`w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-slate-50 transition-colors focus:outline-none ${accordionStep === 3 ? 'rounded-t-xl' : 'rounded-xl'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${accordionStep === 3 ? 'bg-brand-primary' : 'bg-slate-300'}`}>3</div>
                      <span className="font-bold text-slate-800">Endereço</span>
                    </div>
                    {accordionStep === 3 ? <ChevronUp className="w-5 h-5 text-brand-primary" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </button>
                  
                  {accordionStep === 3 && (
                    <div className="px-5 pb-6 pt-4 border-t border-slate-100 space-y-8 animate-fade-in rounded-b-xl">
                      <div className="flex flex-col sm:flex-row gap-8 items-start sm:items-center">
                        <div className="pt-2"><CustomToggle checked={novaParte.endereco.usarCadastrado} onChange={(/** @type {boolean} */ val) => handleNovaParteChange('endereco', 'usarCadastrado', val)} label="Utilizar endereço cadastrado" /></div>
                        <div className="w-full sm:w-1/3"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Locatário</label><select disabled={!novaParte.endereco.usarCadastrado} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 disabled:opacity-50 cursor-pointer"><option>Selecionar locatário principal</option></select></div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                        <div className="md:col-span-3">
                          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">CEP</label>
                          <div className="flex items-center">
                             <input type="text" value={novaParte.endereco.cep} onChange={(e) => handleNovaParteChange('endereco', 'cep', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800" />
                             <button type="button" onClick={(e) => { e.preventDefault(); buscarCepParte(); }} className="text-brand-primary border border-brand-primary/20 rounded-full px-3 py-1 ml-2 text-xs font-bold hover:bg-brand-primary/10 flex items-center gap-1 shadow-sm transition-all focus:outline-none"><Search className="w-3 h-3"/> CEP</button>
                          </div>
                        </div>
                        <div className="md:col-span-4"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Endereço</label><input type="text" value={novaParte.endereco.endereco} onChange={(e) => handleNovaParteChange('endereco', 'endereco', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 transition-colors" /></div>
                        <div className="md:col-span-2"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Número</label><input type="text" value={novaParte.endereco.numero} onChange={(e) => handleNovaParteChange('endereco', 'numero', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 transition-colors" /></div>
                        <div className="md:col-span-3"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Complemento</label><input type="text" value={novaParte.endereco.complemento} onChange={(e) => handleNovaParteChange('endereco', 'complemento', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 transition-colors" /></div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                        <div className="md:col-span-4"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Bairro</label><input type="text" value={novaParte.endereco.bairro} onChange={(e) => handleNovaParteChange('endereco', 'bairro', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 transition-colors" /></div>
                        <div className="md:col-span-4"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Estado</label><select value={novaParte.endereco.estado} onChange={(e) => handleNovaParteChange('endereco', 'estado', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 cursor-pointer"><option>Santa Catarina</option><option>São Paulo</option></select></div>
                        <div className="md:col-span-4"><label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Cidade</label><select value={novaParte.endereco.cidade} onChange={(e) => handleNovaParteChange('endereco', 'cidade', e.target.value)} className="w-full px-1 py-1.5 border-b border-slate-300 bg-transparent focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 cursor-pointer"><option>Florianópolis</option><option>São José</option></select></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ACORDEÃO 4: DOCUMENTOS */}
                <div className="border border-slate-200 rounded-xl bg-white shadow-sm transition-all">
                  <button type="button" onClick={() => setAccordionStep(4)} className={`w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-slate-50 transition-colors focus:outline-none ${accordionStep === 4 ? 'rounded-t-xl' : 'rounded-xl'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${accordionStep === 4 ? 'bg-brand-primary' : 'bg-slate-300'}`}>4</div>
                      <span className="font-bold text-slate-800">Documentos</span>
                    </div>
                    {accordionStep === 4 ? <ChevronUp className="w-5 h-5 text-brand-primary" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </button>
                  
                  {accordionStep === 4 && (
                    <div className="px-5 pb-6 pt-4 border-t border-slate-100 space-y-4 animate-fade-in rounded-b-xl">
                      
                      <div>
                        <input type="file" multiple className="hidden" id="file-upload" onChange={handleFileUpload} />
                        <label htmlFor="file-upload" className="inline-flex items-center justify-center gap-2 px-5 py-2 text-sm font-bold text-brand-primary border border-brand-primary rounded-full hover:bg-brand-primary hover:text-white transition-colors mb-4 cursor-pointer shadow-sm">
                          <Upload className="w-4 h-4"/> Selecionar arquivos...
                        </label>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500">
                              <th className="p-3 w-20">Ações</th>
                              <th className="p-3 whitespace-nowrap">Nome do arquivo</th>
                              <th className="p-3 whitespace-nowrap">Classificar documento</th>
                              <th className="p-3 w-64 whitespace-nowrap">Observações</th>
                              <th className="p-3 w-48 whitespace-nowrap">Avaliações</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {novaParte.documentos.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="p-4 text-center text-sm text-slate-400 font-bold italic">Nenhum documento anexado.</td>
                              </tr>
                            ) : (
                              novaParte.documentos.map((/** @type {any} */ doc, index) => (
                                <tr key={doc.id} className="hover:bg-slate-50 transition-colors">
                                  <td className="p-3 flex gap-3">
                                    <button 
                                       type="button"
                                       onClick={() => handlePreviewFile(doc)} 
                                       className="focus:outline-none text-brand-primary hover:text-teal-600 transition-colors"
                                       title="Visualizar documento"
                                    >
                                      <Eye className="w-4 h-4 cursor-pointer"/>
                                    </button>
                                    <button 
                                       type="button"
                                       className="focus:outline-none text-red-400 hover:text-red-600 transition-colors"
                                      onClick={() => {
                                        setNovaParte(prev => ({...prev, documentos: prev.documentos.filter((/** @type {any} */ d) => d.id !== doc.id)}));
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </td>
                                  
                                  <td className="p-3">
                                    <div className="flex items-center relative">
                                      <input 
                                         type="text" 
                                         value={doc.nome} 
                                         onChange={(e) => {
                                          setNovaParte(prev => {
                                            const newDocs = [...prev.documentos];
                                            newDocs[index].nome = e.target.value;
                                            return {...prev, documentos: newDocs};
                                          });
                                        }}
                                         className="w-full bg-transparent border-b border-dashed border-slate-300 text-xs lg:text-sm font-bold text-slate-800 focus:outline-none focus:border-brand-primary pb-1 pr-6" 
                                         title="Clique para renomear"
                                      />
                                      <Edit3 className="w-3 h-3 text-slate-400 absolute right-1 bottom-1.5 pointer-events-none" />
                                    </div>
                                  </td>
                                  <td className="p-3">
                                    <select 
                                       value={doc.tipo}
                                       onChange={(e) => {
                                        setNovaParte(prev => {
                                          const newDocs = [...prev.documentos];
                                          newDocs[index].tipo = e.target.value;
                                          return {...prev, documentos: newDocs};
                                        });
                                      }}
                                       className="bg-transparent border-b border-slate-300 w-full focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 pb-1 cursor-pointer"
                                    >
                                      <option value="Documentos Pessoais">Documentos Pessoais</option>
                                      <option value="Comprovante de Renda">Comprovante de Renda</option>
                                      <option value="Comprovante de Residência">Comprovante de Residência</option>
                                    </select>
                                  </td>
                                  <td className="p-3">
                                    <input 
                                       type="text" 
                                       value={doc.obs} 
                                      onChange={(e) => {
                                        setNovaParte(prev => {
                                          const newDocs = [...prev.documentos];
                                          newDocs[index].obs = e.target.value;
                                          return {...prev, documentos: newDocs};
                                        });
                                      }}
                                      className="bg-transparent border-b border-slate-300 w-full focus:outline-none focus:border-brand-primary text-sm font-bold text-slate-800 pb-1" 
                                     />
                                  </td>
                                  <td className="p-3 flex items-center gap-2">
                                    <EvalToggle 
                                       checked={doc.status} 
                                       onChange={(/** @type {boolean} */ val) => {
                                        setNovaParte(prev => {
                                          const newDocs = [...prev.documentos];
                                          newDocs[index].status = val;
                                          return {...prev, documentos: newDocs};
                                        });
                                      }} 
                                     />
                                    {!doc.status && (
                                      <input type="text" placeholder="Informe o parecer" className="bg-transparent border-b border-red-500 w-full focus:outline-none text-xs text-red-700 placeholder-red-300 pb-1 animate-fade-in font-bold" />
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

              </div>

              <div className="p-4 sm:p-6 border-t border-slate-200 bg-white flex justify-end gap-3 shrink-0 rounded-b-2xl">
                <button type="button" onClick={accordionStep === 1 ? () => setIsModalOpen(false) : handleAccordionPrev} className="px-6 py-2 text-sm font-bold border border-slate-200 text-slate-600 bg-slate-50 rounded-full hover:bg-slate-100 transition-colors shadow-sm focus:outline-none">Voltar</button>
                {accordionStep < 4 ? (
                  <button type="button" onClick={handleAccordionNext} className="px-6 py-2 text-sm font-bold bg-brand-primary text-white rounded-full hover:bg-teal-600 transition-colors shadow-md shadow-brand-primary/30 focus:outline-none">Avançar</button>
                ) : (
                  <button type="button" onClick={salvarNovaParte} className="px-6 py-2 text-sm font-bold bg-brand-primary text-white rounded-full hover:bg-teal-600 transition-colors shadow-md shadow-brand-primary/30 focus:outline-none">
                    {editandoParteId ? "Salvar Edição" : "Adicionar Parte"}
                  </button>
                )}
              </div>
              
            </div>
          </div>
        </SafePortal>
      )}

      {/* MODAL DE CONFIRMAÇÃO FINAL */}
      {showConfirmModal && (
        <SafePortal>
          <div 
            className="flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4 sm:p-6"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', zIndex: 999999 }}
          >
            <div className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 p-6 text-center">
              
              {isSaving ? (
                <div className="py-12 flex flex-col items-center">
                  <Loader2 className="w-16 h-16 text-brand-primary animate-spin mb-4" />
                  <h2 className="text-2xl font-bold text-slate-800 uppercase">Enviando...</h2>
                  <p className="text-sm font-bold text-slate-500 mt-2">{uploadProgress}</p>
                </div>
              ) : !isValid ? (
                <>
                  <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 text-red-500 mx-auto flex items-center justify-center mb-4">
                    <AlertTriangle className="w-8 h-8" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-800 mb-2">Faltam Informações!</h2>
                  <p className="text-sm font-medium text-slate-500 mb-6">
                    Para enviar a Solicitação Real Benefícios, preencha os seguintes itens:
                  </p>
                  <ul className="text-sm font-bold text-red-600 bg-red-50 border border-red-100 p-4 rounded-xl mb-6 text-left space-y-2">
                    {validationErrors.map((err, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="mt-0.5">•</span> {err}
                      </li>
                    ))}
                  </ul>
                  <button type="button" onClick={() => setShowConfirmModal(false)} className="w-full px-6 py-3 text-sm font-bold bg-slate-100 text-slate-600 rounded-full hover:bg-slate-200 transition-colors border border-slate-200 shadow-sm focus:outline-none">
                    Voltar e Revisar
                  </button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-green-50 border border-green-100 text-green-500 mx-auto flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-800 mb-2">Tudo Certo!</h2>
                  <p className="text-sm font-medium text-slate-500 mb-4 leading-relaxed">
                    A sua Solicitação para o imóvel <strong>{formData.imovel.codigo || formData.imovel.endereco}</strong> contendo <strong>{formData.partes.length} parte(s)</strong> está pronta para análise.
                  </p>

                  {/* LGPD Checkbox Injetado Aqui */}
                  <div className="mb-6 flex items-start gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200 text-left">
                    <div className="pt-0.5 shrink-0">
                       <CustomToggle checked={aceiteTermos} onChange={setAceiteTermos} />
                    </div>
                    <p className="text-xs text-slate-600 font-medium leading-relaxed">
                      Declaro que as informações prestadas são verdadeiras e autorizo a análise de dados. Li e aceito os <span className="text-brand-primary font-bold cursor-pointer hover:underline">Termos de Uso</span> e a <span className="text-brand-primary font-bold cursor-pointer hover:underline">Política de Privacidade</span> (LGPD).
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <button type="button" onClick={() => setShowConfirmModal(false)} disabled={isSaving} className="flex-1 px-6 py-3 text-sm font-bold border border-slate-200 text-slate-600 bg-slate-50 rounded-full hover:bg-slate-100 transition-colors disabled:opacity-50 shadow-sm focus:outline-none">
                      Cancelar
                    </button>
                    <button 
                      type="button" 
                      onClick={handleFinish} 
                      disabled={isSaving || !aceiteTermos} 
                      className={`flex-1 px-6 py-3 text-sm font-bold text-white rounded-full transition-colors flex items-center justify-center gap-2 focus:outline-none ${isSaving || !aceiteTermos ? 'bg-slate-300 cursor-not-allowed' : 'bg-brand-primary hover:bg-teal-600 shadow-md shadow-brand-primary/30'}`}
                    >
                      {isSaving ? "Enviando..." : (id ? "Reenviar Edição" : "Confirmar e Enviar")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </SafePortal>
      )}

    </div>
  );
};

export default NovaSolicitacaoPage;